import type { VariantProps } from '@joyful/utils'
import { tv } from '@joyful/utils'
import { InfoIcon as InfoCircleIcon } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { useId } from 'react'
import type { IconButtonProps } from './Button'
import { IconButton } from './Button'
import type { HoverCardContentProps, HoverCardProps } from './HoverCard'
import { HoverCard, HoverCardContent, HoverCardTrigger } from './HoverCard'
import type { TooltipProps } from './Tooltip'
import { Tooltip } from './Tooltip'

export const infoHoverCardIconVariants = tv({
  base: 'inline-flex size-auto cursor-default',

  defaultVariants: {
    tone: 'default',
  },

  variants: {
    tone: {
      content: 'text-tremor-content',
      default: null,
      emphasis: 'text-tremor-content-emphasis',
    },
  },
})

type InfoIconProps = Omit<ComponentProps<'button'>, 'className'> &
  Pick<IconButtonProps, 'size'> &
  VariantProps<typeof infoHoverCardIconVariants>

function InfoIcon({ size, tone, ...props }: InfoIconProps) {
  return (
    <span className={infoHoverCardIconVariants({ tone })}>
      <IconButton
        colorVariant="muted"
        tabIndex={0}
        variant="ghost"
        size={size ?? 'xs'}
        {...props}
      >
        <InfoCircleIcon />
      </IconButton>
    </span>
  )
}

export interface InfoHoverCardProps
  extends HoverCardProps, Pick<InfoIconProps, 'size' | 'tone'> {
  children: ReactNode
  contentProps?: Omit<HoverCardContentProps, 'children' | 'className' | 'id'>
}

export function InfoHoverCard({
  children,
  contentProps,
  size,
  tone,
  ...hovercardProps
}: InfoHoverCardProps) {
  const id = useId()

  return (
    <HoverCard openDelay={300} {...hovercardProps}>
      <HoverCardTrigger asChild>
        <InfoIcon
          aria-describedby={id}
          aria-label={typeof children === 'string' ? children : undefined}
          size={size}
          tone={tone}
        />
      </HoverCardTrigger>
      <HoverCardContent id={id} size="sm" textSize="xs" {...contentProps}>
        {children}
      </HoverCardContent>
    </HoverCard>
  )
}

export interface InfoHoverCardTooltipProps extends Omit<
  TooltipProps,
  'children' | 'content' | 'contentProps'
> {
  children: ReactNode
  contentProps?: Omit<
    NonNullable<TooltipProps['contentProps']>,
    'className' | 'id'
  >
  label: string
  size?: InfoIconProps['size']
  tone?: InfoIconProps['tone']
}

export function InfoHoverCardTooltip({
  children,
  contentProps,
  label,
  size,
  tone,
  ...tooltipProps
}: InfoHoverCardTooltipProps) {
  const id = useId()
  return (
    <Tooltip
      content={children}
      {...tooltipProps}
      tooltipRootProps={{
        delayDuration: 50,
        ...tooltipProps?.tooltipRootProps,
      }}
      contentProps={{
        ...contentProps,
        id,
      }}
    >
      <InfoIcon
        aria-describedby={id}
        aria-label={label}
        size={size}
        tone={tone}
      />
    </Tooltip>
  )
}
